import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import gql from 'graphql-tag';

import { useAppState } from "../../contexts/AppContext";
import { FLAG_ZITADEL_AZURE_LOGIN } from "../../includes/featureFlags";
import { hasFeatureFlag } from "../../includes/site";
import { AUTH_URL, getUrl } from "../../includes/routes";
import { API_BASE_URL } from "../../../config/config";
import {
    FAILED_AUTHENTICATION_PARAM,
    ZITADEL_IDP_AUTH_BACK_URL_PARAM,
    ZITADEL_IDP_AUTH_INTENT_ID_PARAM,
    ZITADEL_IDP_AUTH_INTENT_TOKEN_URL_PARAM,
    ZITADEL_IDP_AUTH_IS_APP_REDIRECT_DISABLED_PARAM,
    ZITADEL_IDP_AUTH_SCHOOL_UNIQUE_KEY_PARAM,
} from "../../includes/constants";
import { LoginInfoManager } from "../../includes/loginInfoManager";

type ZitadelLoginWithIdpMutationResponse = {
    zitadelLoginWithIdp: {
        LastLoginType: string;
        id: string;
    }
}

type ZitadelLoginWithIdpMutationVariables = {
    intentId: string;
    intentToken: string;
    schoolUniqueKey: string;
}

const IdpAuthLanding = () => {
    const [appState] = useAppState();
    const isFeatureFlagsLoading = appState.isFeatureFlagsLoading;
    const hasZitadelAzureLoginFeatureFlag = hasFeatureFlag(appState.featureFlags, FLAG_ZITADEL_AZURE_LOGIN);

    const [searchParams] = useSearchParams();
    const intentId = searchParams.get(ZITADEL_IDP_AUTH_INTENT_ID_PARAM) ?? "";
    const intentToken = searchParams.get(ZITADEL_IDP_AUTH_INTENT_TOKEN_URL_PARAM) ?? "";
    const isRedirectDisabled = searchParams.has(ZITADEL_IDP_AUTH_IS_APP_REDIRECT_DISABLED_PARAM);
    const backUrl = searchParams.get(ZITADEL_IDP_AUTH_BACK_URL_PARAM) ?? "";
    const schoolUniqueKey = searchParams.get(ZITADEL_IDP_AUTH_SCHOOL_UNIQUE_KEY_PARAM) ?? "";

    const [zitadelLoginWithIdp] =
        useMutation<ZitadelLoginWithIdpMutationResponse, ZitadelLoginWithIdpMutationVariables>(ZITADEL_LOGIN_WITH_IDP);

    const [isAuthenticationComplete, setIsAuthenticationComplete] = useState(false);

    useEffect(function redirectUser() {
        if (isFeatureFlagsLoading) return;

        if (!hasZitadelAzureLoginFeatureFlag) window.location.href = getUrl(AUTH_URL);

        if (!intentId || !intentToken) {
            LoginInfoManager.clear();
            window.location.href = getUrl(AUTH_URL) + "?" + FAILED_AUTHENTICATION_PARAM;
        }

        if (isAuthenticationComplete) return;

        zitadelLoginWithIdp({
            variables: {
                intentId,
                intentToken,
                schoolUniqueKey,
            },
        }).then(({ data }) => {
            if (!data) throw new Error("Login attempt did not return a valid response.");

            setIsAuthenticationComplete(true);

            if (!isRedirectDisabled) {
                let url = API_BASE_URL + "/auth/login-redirect";
                if (backUrl) url += "?BackURL=" + backUrl;
                window.location.href = url;
            }
        }).catch(() => {
            LoginInfoManager.clear();
            window.location.href = getUrl(AUTH_URL) + "?" + FAILED_AUTHENTICATION_PARAM;
        })
    }, [
        isFeatureFlagsLoading,
        hasZitadelAzureLoginFeatureFlag,
        zitadelLoginWithIdp,
        intentId,
        intentToken,
        isRedirectDisabled,
        isAuthenticationComplete,
        backUrl,
        schoolUniqueKey,
    ]);

    return (
        <div>
            {isAuthenticationComplete ?
                "Authentication complete!" + (isRedirectDisabled ? " You may return to your window" : "") :
                "Completing authentication..."
            }
        </div>
    );
};

const ZITADEL_LOGIN_WITH_IDP = gql`
    mutation zitadelLoginWithIdp($intentId: String!, $intentToken: String!, $schoolUniqueKey: String!) {
        zitadelLoginWithIdp(intentId: $intentId, intentToken: $intentToken, schoolUniqueKey: $schoolUniqueKey) {
            id
        }
    }
`;

export default IdpAuthLanding;
